import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Grid, Card, TextField, CircularProgress, Alert, LinearProgress, Chip
} from '@mui/material';
import {
  CheckCircle, ArrowForward, ArrowBack, Person, Phone, Email, LocationCity, VerifiedUser
} from '@mui/icons-material';

interface WizardProps {
  open: boolean;
  onClose: () => void;
  serviceCategory: string; // 'startups' | 'income-tax' | 'tax-planning' | 'gst' | 'mca'
  initialServiceName?: string;
}

const ServiceDiagnosticWizard: React.FC<WizardProps> = ({
  open,
  onClose,
  serviceCategory,
  initialServiceName
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Diagnostic Answers State
  const [answers, setAnswers] = useState<Record<string, any>>({});

  // Client Contact Details State
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');

  const handleSelectOption = (questionKey: string, optionValue: string) => {
    setAnswers(prev => ({ ...prev, [questionKey]: optionValue }));
  };

  const isNextDisabled = () => {
    if (step === 1 && !answers.q1) return true;
    if (step === 2 && !answers.q2) return true;
    if (step === 3 && !answers.q3) return true;
    if (step === 4 && (!name || !phone || phone.length < 10)) return true;
    return false;
  };

  const handleSubmit = async () => {
    if (!name || !phone) {
      setError('Please provide your Name and valid 10-digit Mobile Number.');
      return;
    }

    setLoading(true);
    setError('');

    const leadPayload = {
      serviceCategory,
      selectedService: initialServiceName || serviceCategory,
      clientName: name,
      clientPhone: phone,
      clientEmail: email,
      clientCity: city,
      diagnosticAnswers: answers,
      timestamp: new Date().toISOString()
    };

    try {
      const apiBase = process.env.REACT_APP_API_URL || 
        (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'
          : 'https://fingard-backend.onrender.com');

      const response = await fetch(`${apiBase}/api/register-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadPayload)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        // Fallback success UI if backend offline
        setSubmitted(true);
      }
    } catch (err) {
      // Fallback grace
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setAnswers({});
    setName('');
    setPhone('');
    setEmail('');
    setCity('');
    setSubmitted(false);
    setError('');
    onClose();
  };

  // Get diagnostic question tree based on serviceCategory
  const getQuestions = () => {
    switch (serviceCategory) {
      case 'startups':
        return {
          title: 'Startup & Incorporation Diagnostic Wizard',
          subtitle: 'Answer 3 quick questions to find your optimal business entity structure',
          q1: {
            key: 'q1',
            question: 'How many co-founders / partners are involved in this business?',
            options: [
              { label: 'Single Founder (1 Person)', value: 'Single Founder', desc: 'Suitable for OPC or Sole Proprietorship' },
              { label: '2 to 5 Co-Founders', value: '2-5 Founders', desc: 'Suitable for Private Limited or LLP' },
              { label: 'Family Owned Business', value: 'Family Business', desc: 'Suitable for HUF, Partnership or Pvt Ltd' }
            ]
          },
          q2: {
            key: 'q2',
            question: 'Do you plan to raise external VC / Angel investment capital?',
            options: [
              { label: 'Yes, actively planning to raise equity funding', value: 'VC Funded Plan', desc: 'Pvt Ltd is mandatory for issuing ESOPs & shares' },
              { label: 'No, self-funded / bootstrapped for now', value: 'Bootstrapped', desc: 'LLP or Proprietorship can save compliance cost' },
              { label: 'Undecided / Future scope', value: 'Future Scope', desc: 'Start flexible or incorporate Pvt Ltd' }
            ]
          },
          q3: {
            key: 'q3',
            question: 'Do you require Trademark & Brand Name Protection?',
            options: [
              { label: 'Yes, want brand name & logo registered', value: 'Need Trademark', desc: 'Includes TM search & Class filing' },
              { label: 'No, incorporation only for now', value: 'No Trademark', desc: 'Basic incorporation package' }
            ]
          }
        };

      case 'income-tax':
        return {
          title: 'Income Tax Diagnostic & Compliance Wizard',
          subtitle: 'Assess your ITR form type, tax notice status, and savings potential',
          q1: {
            key: 'q1',
            question: 'What is your primary source of annual income?',
            options: [
              { label: 'Salaried Employee (Form 16)', value: 'Salaried', desc: 'ITR-1 / ITR-2 filing required' },
              { label: 'Business / Profession / Freelancer', value: 'Business Freelancer', desc: 'Sec 44ADA / 44AD or Full Audit Books' },
              { label: 'F&O Derivatives, Crypto & Stock Trader', value: 'Trader F&O', desc: 'ITR-3 Business Return with loss set-off' },
              { label: 'NRI / Foreign Assets / Director', value: 'NRI Foreign', desc: 'ITR-2 / ITR-3 specialized filing' }
            ]
          },
          q2: {
            key: 'q2',
            question: 'Have you received any Income Tax Notice or Defective Return Demand?',
            options: [
              { label: 'Yes, received notice u/s 143(1), 139(9), or 148', value: 'Notice Received', desc: 'Requires CA notice response drafting' },
              { label: 'No, standard regular filing', value: 'No Notice', desc: 'Regular annual ITR filing' }
            ]
          },
          q3: {
            key: 'q3',
            question: 'What is your estimated Gross Annual Income bracket?',
            options: [
              { label: 'Up to ₹7.5 Lakhs (Rebate Available)', value: 'Below 7.5L', desc: 'Zero or minimal tax' },
              { label: '₹7.5 Lakhs to ₹15 Lakhs', value: '7.5L - 15L', desc: 'Moderate tax savings potential' },
              { label: 'Above ₹15 Lakhs (High Bracket)', value: 'Above 15L', desc: 'Maximum tax savings & restructuring needed' }
            ]
          }
        };

      case 'tax-planning':
        return {
          title: 'Tax Planning & Advisory Diagnostic Wizard',
          subtitle: 'Identify your tax saving potential & custom advisory tier',
          q1: {
            key: 'q1',
            question: 'What is your primary employment or income profile?',
            options: [
              { label: 'Salaried Corporate Executive', value: 'Salaried Executive', desc: 'HRA, LTA, 80C, 80D, NPS optimization' },
              { label: 'Independent Consultant / Freelancer', value: 'Consultant', desc: 'Sec 44ADA 50% presumptive tax structuring' },
              { label: 'Business Owner / Director', value: 'Business Director', desc: 'Corporate tax, salary vs dividend planning' }
            ]
          },
          q2: {
            key: 'q2',
            question: 'What is your primary goal for Tax Advisory?',
            options: [
              { label: 'Immediate Tax Savings before March 31', value: 'Immediate Savings', desc: 'Quick 1-on-1 CA consultation' },
              { label: 'Year-Round Quarterly Tax Review & Retainer', value: 'Year Round Retainer', desc: 'Continuous compliance & document prep' },
              { label: 'Capital Gains & Wealth Restructuring', value: 'Capital Gains Structuring', desc: 'Property sale / Stock portfolio planning' }
            ]
          },
          q3: {
            key: 'q3',
            question: 'Are you looking for Audit Support & Legal Notice Protection?',
            options: [
              { label: 'Yes, want full year CA retainer with audit support', value: 'Full Audit Protection', desc: 'Premium CA Retainer Tier' },
              { label: 'No, basic tax strategy report only', value: 'Strategy Only', desc: 'Basic Tax Planning Package' }
            ]
          }
        };

      case 'gst':
        return {
          title: 'GST Compliance & Registration Diagnostic Wizard',
          subtitle: 'Determine your GST liability, filing scheme, and return frequency',
          q1: {
            key: 'q1',
            question: 'What is your current GST requirement?',
            options: [
              { label: 'New GST Registration', value: 'New Registration', desc: 'Fresh GSTIN allotment' },
              { label: 'Monthly / Quarterly Return Filing', value: 'Regular Filing', desc: 'GSTR-1, 3B & 2B Reconciliation' },
              { label: 'GST Notice Response / Legal SCN', value: 'GST Notice', desc: 'Departmental notice response & hearing' },
              { label: 'GST Cancellation / Amendment', value: 'Cancellation Amendment', desc: 'Modify address/directors or cancel GSTIN' }
            ]
          },
          q2: {
            key: 'q2',
            question: 'Are you selling on E-Commerce (Amazon/Flipkart) or Exporting Services?',
            options: [
              { label: 'Yes, E-commerce Seller or Software Exporter', value: 'Ecom Export', desc: 'Mandatory GST + LUT Certificate required' },
              { label: 'No, Domestic offline / local B2B & B2C business', value: 'Domestic Local', desc: 'Standard turnover threshold applies' }
            ]
          },
          q3: {
            key: 'q3',
            question: 'What is your annual business turnover?',
            options: [
              { label: 'Up to ₹20 Lakhs / ₹40 Lakhs', value: 'Below Threshold', desc: 'Optional or voluntary GST' },
              { label: '₹20 Lakhs to ₹1.5 Crore', value: '20L - 1.5Cr', desc: 'Composition or Regular scheme' },
              { label: 'Above ₹1.5 Crore', value: 'Above 1.5Cr', desc: 'Mandatory Regular GSTR-1 & 3B Monthly' }
            ]
          }
        };

      case 'mca':
      default:
        return {
          title: 'MCA Corporate Compliance Diagnostic Wizard',
          subtitle: 'Check mandatory ROC filings, DIN eKYC, and penalty risks for your company',
          q1: {
            key: 'q1',
            question: 'What is your registered entity structure?',
            options: [
              { label: 'Private Limited Company / OPC', value: 'Pvt Ltd OPC', desc: 'AOC-4, MGT-7, ADT-1, DIN eKYC mandatory' },
              { label: 'Limited Liability Partnership (LLP)', value: 'LLP Structure', desc: 'Form 11, Form 8, DIR-3 eKYC mandatory' },
              { label: 'Public Company / Section 8 NGO', value: 'Public Sec8', desc: 'Full statutory compliance' }
            ]
          },
          q2: {
            key: 'q2',
            question: 'Which statutory filings are currently pending or due?',
            options: [
              { label: 'Annual Financial Statements (AOC-4 / Form 8)', value: 'AOC4 Form8', desc: 'Annual financial compliance' },
              { label: 'Director eKYC (DIR-3 KYC) / DIN Reactivation', value: 'DIN eKYC', desc: 'Director KYC compliance' },
              { label: 'Director Addition / Removal / Office Change', value: 'Corporate Modification', desc: 'Form DIR-12 / INC-22' },
              { label: 'DPT-3 Loan Return / ADT-1 Auditor Appointment', value: 'DPT3 ADT1', desc: 'Specific compliance returns' }
            ]
          },
          q3: {
            key: 'q3',
            question: 'Is your company facing daily MCA late penalty fines?',
            options: [
              { label: 'Yes, filings are overdue (₹100/day penalty running)', value: 'Late Penalties Running', desc: 'Urgent regularization required' },
              { label: 'No, filing within due dates', value: 'On Time Filing', desc: 'Standard compliance package' }
            ]
          }
        };
    }
  };

  const wizardData = getQuestions();

  const renderStepContent = () => {
    if (step === 1) {
      const q = wizardData.q1;
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" color="#123D6B" gutterBottom>
            Step 1 of 4: {q.question}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {q.options.map((opt, i) => (
              <Grid item xs={12} key={i}>
                <Card
                  variant="outlined"
                  onClick={() => handleSelectOption('q1', opt.value)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    borderColor: answers.q1 === opt.value ? '#123D6B' : '#E2E8F0',
                    bgcolor: answers.q1 === opt.value ? 'rgba(18, 61, 107, 0.05)' : '#ffffff',
                    borderWidth: answers.q1 === opt.value ? 2 : 1,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#C59B27', bgcolor: '#F8FAFC' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                        {opt.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.desc}
                      </Typography>
                    </Box>
                    {answers.q1 === opt.value && <CheckCircle color="primary" />}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (step === 2) {
      const q = wizardData.q2;
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" color="#123D6B" gutterBottom>
            Step 2 of 4: {q.question}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {q.options.map((opt, i) => (
              <Grid item xs={12} key={i}>
                <Card
                  variant="outlined"
                  onClick={() => handleSelectOption('q2', opt.value)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    borderColor: answers.q2 === opt.value ? '#123D6B' : '#E2E8F0',
                    bgcolor: answers.q2 === opt.value ? 'rgba(18, 61, 107, 0.05)' : '#ffffff',
                    borderWidth: answers.q2 === opt.value ? 2 : 1,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#C59B27', bgcolor: '#F8FAFC' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                        {opt.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.desc}
                      </Typography>
                    </Box>
                    {answers.q2 === opt.value && <CheckCircle color="primary" />}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (step === 3) {
      const q = wizardData.q3;
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" color="#123D6B" gutterBottom>
            Step 3 of 4: {q.question}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {q.options.map((opt, i) => (
              <Grid item xs={12} key={i}>
                <Card
                  variant="outlined"
                  onClick={() => handleSelectOption('q3', opt.value)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: 2,
                    borderColor: answers.q3 === opt.value ? '#123D6B' : '#E2E8F0',
                    bgcolor: answers.q3 === opt.value ? 'rgba(18, 61, 107, 0.05)' : '#ffffff',
                    borderWidth: answers.q3 === opt.value ? 2 : 1,
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#C59B27', bgcolor: '#F8FAFC' }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                        {opt.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.desc}
                      </Typography>
                    </Box>
                    {answers.q3 === opt.value && <CheckCircle color="primary" />}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    if (step === 4) {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" color="#123D6B" gutterBottom>
            Final Step: Receive Your Diagnostic Report & CA Estimate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Our Chartered Accountants will analyze your diagnostic profile and get in touch with your custom compliance plan.
          </Typography>

          <Grid container spacing= {2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                placeholder="e.g. Rahul Sharma"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                InputProps={{ startAdornment: <Person sx={{ color: '#64748b', mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number (WhatsApp)"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                required
                InputProps={{ startAdornment: <Phone sx={{ color: '#64748b', mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                placeholder="rahul@example.com"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                InputProps={{ startAdornment: <Email sx={{ color: '#64748b', mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City / State"
                placeholder="e.g. Mumbai, Maharashtra"
                value={city}
                onChange={e => setCity(e.target.value)}
                InputProps={{ startAdornment: <LocationCity sx={{ color: '#64748b', mr: 1 }} /> }}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetWizard}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="#123D6B">
              {wizardData.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {wizardData.subtitle}
            </Typography>
          </Box>
          <Chip label={`Step ${step} of 4`} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
        </Box>
        <LinearProgress variant="determinate" value={(step / 4) * 100} sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: '#e2e8f0' }} />
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <VerifiedUser sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="#0f172a" gutterBottom>
              Diagnostic Profile Registered!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 3 }}>
              Thank you, <strong>{name}</strong>. Our senior Chartered Accountant is reviewing your parameters (<strong>{answers.q1}</strong>, <strong>{answers.q2}</strong>). We will call you on <strong>+91 {phone}</strong> shortly.
            </Typography>
            <Button
              variant="contained"
              onClick={resetWizard}
              sx={{ py: 1.2, px: 4, borderRadius: 2, bgcolor: '#123D6B', fontWeight: 'bold' }}
            >
              Done & Return to Services
            </Button>
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {renderStepContent()}
          </>
        )}
      </DialogContent>

      {!submitted && (
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          {step > 1 ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => setStep(prev => prev - 1)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
            >
              Back
            </Button>
          ) : <Box />}

          {step < 4 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={isNextDisabled()}
              onClick={() => setStep(prev => prev + 1)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', bgcolor: '#123D6B', '&:hover': { bgcolor: '#C59B27' } }}
            >
              Continue to Step {step + 1}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={loading || isNextDisabled()}
              onClick={handleSubmit}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', bgcolor: '#10b981', px: 3, '&:hover': { bgcolor: '#059669' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Diagnostic Profile'}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ServiceDiagnosticWizard;
